import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import apiClient from "../constants/apiClient";

export interface CreateDeviceData {
    name?: string;
    UID?: string;
    type?: string;
    fruit_type?: string;
}

interface Device {
    UID: string;
    name: string;
    type: string;
    tenant_id?: string;
    joinstatus?: string;
    last_seen?: string;
    tenant?: {
        tenant_name: string;
    };
}

// Query for fetching all devices
export function useDevicesQuery() {
    return useQuery({
        queryKey: ["devices"],
        queryFn: async (): Promise<Device[]> => {
            const response = await apiClient.get("/device/management/");
            return response.data;
        },
        staleTime: 30000, // Consider data stale after 30 seconds
        refetchInterval: 60000, // Refetch every minute
    });
}

// Query for fetching a single device
export function useDeviceQuery(deviceUid: string) {
    return useQuery({
        queryKey: ["device", deviceUid],
        queryFn: async (): Promise<Device> => {
            const response = await apiClient.get(`/device/management/${deviceUid}/`);
            return response.data;
        },
        enabled: !!deviceUid,
    });
}

export function useCreateDeviceMutation() {
    const queryClient = useQueryClient();
    const navigate = useNavigate();

    return useMutation({
        mutationFn: async (deviceData: CreateDeviceData): Promise<Device> => {
            const response = await apiClient.post("/device/management/", deviceData);
            return response.data;
        },
        onSuccess: (data) => {
            // Invalidate and refetch devices list
            queryClient.invalidateQueries({ queryKey: ["devices"] });

            // Navigate to the created device
            navigate(`/device-management/${data.UID}`);
        },
        onError: (error) => {
            console.error("Failed to create device:", error); // eslint-disable-line no-console
        }
    });
}
